import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Download, Search, Filter, Eye, EyeOff, Shield } from 'lucide-react';
import { auditService } from '../services/auditService';
import { AuditLogFilters } from '../types';
import toast from 'react-hot-toast';

const AuditLogsPage: React.FC = () => {
  const [filters, setFilters] = useState<AuditLogFilters>({
    pageNumber: 1,
    pageSize: 25, // Increased default page size for better performance
  });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Fetch audit logs
  const { data: auditLogsResponse, isLoading, error, refetch } = useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: () => auditService.getAuditLogs(filters),
    enabled: true,
  });

  // Fetch filter options with caching
  const { data: usersResponse } = useQuery({
    queryKey: ['auditLogsUsers'],
    queryFn: auditService.getDistinctUsers,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: actionsResponse } = useQuery({
    queryKey: ['auditLogsActions'],
    queryFn: auditService.getDistinctActions,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: entityTypesResponse } = useQuery({
    queryKey: ['auditLogsEntityTypes'],
    queryFn: auditService.getDistinctEntityTypes,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: logLevelsResponse } = useQuery({
    queryKey: ['auditLogsLogLevels'],
    queryFn: auditService.getDistinctLogLevels,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: errorCodesResponse } = useQuery({
    queryKey: ['auditLogsErrorCodes'],
    queryFn: auditService.getDistinctErrorCodes,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const { data: companiesResponse } = useQuery({
    queryKey: ['auditLogsCompanies'],
    queryFn: auditService.getDistinctCompanies,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  const handleFilterChange = useCallback((key: keyof AuditLogFilters, value: any) => {
    // Convert "all" values to undefined for proper filtering
    const filterValue = value === "all" ? undefined : value;

    setFilters(prev => {
      // Determine the new page number based on what's changing
      let newPageNumber = prev.pageNumber;
      if (key === 'pageNumber') {
        // Changing page number - use the new value
        newPageNumber = filterValue;
      } else if (key !== 'pageSize') {
        // Changing a filter (not pageSize, not pageNumber) - reset to page 1
        newPageNumber = 1;
      }
      // If changing pageSize, keep current page number

      return {
        ...prev,
        [key]: filterValue,
        pageNumber: newPageNumber,
      };
    });
  }, []);

  // Normalize timestamps that may be UTC without timezone and display in local time
  const formatLocalTimestamp = useCallback((timestamp: string) => {
    if (!timestamp) return '';
    const iso = /Z|[\+\-]\d{2}:?\d{2}$/.test(timestamp) ? timestamp : `${timestamp}Z`;
    const date = new Date(iso);
    return date.toLocaleString();
  }, []);

  const handleSearch = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleClearFilters = useCallback(() => {
    setFilters({
      pageNumber: 1,
      pageSize: 25,
    });
  }, []);

  const handleExport = useCallback(async () => {
    try {
      const blob = await auditService.exportAuditLogs(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Audit logs exported successfully');
    } catch (error) {
      toast.error('Failed to export audit logs');
    }
  }, [filters]);

  const toggleRowExpansion = useCallback((id: number) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  const getActionBadgeColor = (action: string) => {
    switch (action.toLowerCase()) {
      case 'created': return 'bg-green-100 text-green-800';
      case 'updated': return 'bg-accent-color-100 text-accent-color-800';
      case 'deleted': return 'bg-red-100 text-red-800';
      case 'login': return 'bg-purple-100 text-purple-800';
      case 'logout': return 'bg-gray-100 text-gray-800';
      case 'failedlogin': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatJsonValue = (jsonString?: string) => {
    if (!jsonString) return null;
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="text-center text-red-600">
              Error loading audit logs: {error.message}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <Shield className="h-6 w-6 text-gray-900 dark:text-gray-100" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Audit Logs</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Track system activity and user actions</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button
            onClick={handleExport}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Search</label>
                <Input
                  placeholder="Search logs..."
                  value={filters.searchTerm || ''}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">User</label>
                <Select
                  value={filters.userId || 'all'}
                  onValueChange={(value) => handleFilterChange('userId', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    <SelectItem value="all" className="text-gray-900 hover:bg-gray-100 cursor-pointer">All users</SelectItem>
                    {usersResponse?.data?.map((user) => (
                      <SelectItem key={user} value={user} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                        {user}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Company</label>
                <Select
                  value={filters.companyId?.toString() || 'all'}
                  onValueChange={(value) => handleFilterChange('companyId', value === 'all' ? undefined : parseInt(value))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All companies" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    <SelectItem value="all" className="text-gray-900 hover:bg-gray-100 cursor-pointer">All companies</SelectItem>
                    {companiesResponse?.data?.map((company) => (
                      <SelectItem key={company.id} value={company.id.toString()} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Action</label>
                <Select
                  value={filters.action || 'all'}
                  onValueChange={(value) => handleFilterChange('action', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    <SelectItem value="all" className="text-gray-900 hover:bg-gray-100 cursor-pointer">All actions</SelectItem>
                    {actionsResponse?.data?.map((action) => (
                      <SelectItem key={action} value={action} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                        {action}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Entity Type</label>
                <Select
                  value={filters.entityType || 'all'}
                  onValueChange={(value) => handleFilterChange('entityType', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All entities" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    <SelectItem value="all" className="text-gray-900 hover:bg-gray-100 cursor-pointer">All entities</SelectItem>
                    {entityTypesResponse?.data?.map((entityType) => (
                      <SelectItem key={entityType} value={entityType} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                        {entityType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Log Level</label>
                <Select
                  value={filters.logLevel || 'all'}
                  onValueChange={(value) => handleFilterChange('logLevel', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All levels" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    <SelectItem value="all" className="text-gray-900 hover:bg-gray-100 cursor-pointer">All levels</SelectItem>
                    {logLevelsResponse?.data?.map((logLevel) => (
                      <SelectItem key={logLevel} value={logLevel} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                        {logLevel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Error Code</label>
                <Select
                  value={filters.errorCode || 'all'}
                  onValueChange={(value) => handleFilterChange('errorCode', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All error codes" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
                    <SelectItem value="all" className="text-gray-900 hover:bg-gray-100 cursor-pointer">All error codes</SelectItem>
                    {errorCodesResponse?.data?.map((errorCode) => (
                      <SelectItem key={errorCode} value={errorCode} className="text-gray-900 hover:bg-gray-100 cursor-pointer">
                        {errorCode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Start Date</label>
                <input
                  type="date"
                  value={filters.startDate ? new Date(filters.startDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('startDate', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">End Date</label>
                <input
                  type="date"
                  value={filters.endDate ? new Date(filters.endDate).toISOString().split('T')[0] : ''}
                  onChange={(e) => handleFilterChange('endDate', e.target.value ? new Date(e.target.value).toISOString() : undefined)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button onClick={handleSearch} className="flex items-center gap-2">
                <Search className="h-4 w-4" />
                Search
              </Button>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Audit Logs
            {auditLogsResponse?.data && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({auditLogsResponse.data.totalCount} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading audit logs...</div>
          ) : (
            <div className="space-y-4">
              {auditLogsResponse?.data?.data?.map((log) => (
                <div key={log.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Badge className={getActionBadgeColor(log.action)}>
                        {log.action}
                      </Badge>
                      {(log.logLevel === 'Error' || log.errorCode) && (
                        <Badge variant="destructive" className="text-xs">
                          {log.logLevel === 'Error' ? 'ERROR' : 'ISSUE'}
                        </Badge>
                      )}
                      {log.logLevel === 'Warning' && (
                        <Badge variant="secondary" className="text-xs">
                          WARNING
                        </Badge>
                      )}
                      <div>
                        <div className="font-medium">{log.userName}</div>
                        <div className="text-sm text-gray-500">
                          {formatLocalTimestamp(log.timestamp)}
                        </div>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">{log.entityType}</span>
                        {log.entityId && <span className="text-gray-500"> #{log.entityId}</span>}
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Company: </span>
                        <span className="font-medium">{log.companyName || `Company ${log.companyId}`}</span>
                      </div>
                      {log.details && (
                        <div className="text-sm text-gray-600 max-w-md truncate">
                          {log.details}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleRowExpansion(log.id)}
                    >
                      {expandedRows.has(log.id) ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {expandedRows.has(log.id) && (
                    <div className="mt-4 pt-4 border-t space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">IP Address</label>
                          <div className="text-sm text-gray-600">{log.ipAddress || 'N/A'}</div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">User Agent</label>
                          <div className="text-sm text-gray-600 truncate">
                            {log.userAgent || 'N/A'}
                          </div>
                        </div>
                      </div>

                      {log.oldValues && (
                        <div>
                          <label className="block text-sm font-medium mb-1">Old Values</label>
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-40">
                            {formatJsonValue(log.oldValues)}
                          </pre>
                        </div>
                      )}

                      {log.newValues && (
                        <div>
                          <label className="block text-sm font-medium mb-1">New Values</label>
                          <pre className="bg-gray-50 p-3 rounded text-xs overflow-auto max-h-40">
                            {formatJsonValue(log.newValues)}
                          </pre>
                        </div>
                      )}

                      {/* Error Details Section */}
                      {(log.logLevel || log.errorCode || log.exceptionDetails || log.requestPath || log.httpMethod || log.httpStatusCode) && (
                        <div className="border-t pt-4">
                          <h4 className="text-sm font-medium mb-3 text-red-600">Error Details</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {log.logLevel && (
                              <div>
                                <label className="block text-sm font-medium mb-1">Log Level</label>
                                <Badge variant={log.logLevel === 'Error' ? 'destructive' : log.logLevel === 'Warning' ? 'secondary' : 'default'}>
                                  {log.logLevel}
                                </Badge>
                              </div>
                            )}
                            {log.errorCode && (
                              <div>
                                <label className="block text-sm font-medium mb-1">Error Code</label>
                                <div className="text-sm text-red-600 font-mono">{log.errorCode}</div>
                              </div>
                            )}
                            {log.httpStatusCode && (
                              <div>
                                <label className="block text-sm font-medium mb-1">HTTP Status</label>
                                <Badge variant={log.httpStatusCode >= 500 ? 'destructive' : log.httpStatusCode >= 400 ? 'secondary' : 'default'}>
                                  {log.httpStatusCode}
                                </Badge>
                              </div>
                            )}
                            {log.httpMethod && (
                              <div>
                                <label className="block text-sm font-medium mb-1">HTTP Method</label>
                                <div className="text-sm text-gray-600 font-mono">{log.httpMethod}</div>
                              </div>
                            )}
                            {log.requestPath && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Request Path</label>
                                <div className="text-sm text-gray-600 font-mono break-all">{log.requestPath}</div>
                              </div>
                            )}
                            {log.exceptionDetails && (
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium mb-1">Exception Details</label>
                                <pre className="bg-red-50 p-3 rounded text-xs overflow-auto max-h-40 text-red-800">
                                  {log.exceptionDetails}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {auditLogsResponse?.data?.data?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No audit logs found
                </div>
              )}

              {/* Pagination */}
              {auditLogsResponse?.data && (
                <div className="flex justify-between items-center mt-6">
                  {/* Page Size Selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Show:</span>
                    <Select
                      value={filters.pageSize.toString()}
                      onValueChange={(value) => handleFilterChange('pageSize', parseInt(value))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg z-50 text-gray-900 hover:bg-gray-100 cursor-pointer">
                        <SelectItem value="10" className="bg-white border border-gray-200 shadow-lg z-50 text-gray-900 hover:bg-gray-100 cursor-pointer">10</SelectItem>
                        <SelectItem value="25" className="bg-white border border-gray-200 shadow-lg z-50 text-gray-900 hover:bg-gray-100 cursor-pointer">25</SelectItem>
                        <SelectItem value="50" className="bg-white border border-gray-200 shadow-lg z-50 text-gray-900 hover:bg-gray-100 cursor-pointer">50</SelectItem>
                        <SelectItem value="100" className="bg-white border border-gray-200 shadow-lg z-50 text-gray-900 hover:bg-gray-100 cursor-pointer">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-gray-600">per page</span>
                  </div>

                  {/* Pagination Info and Controls */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">
                      {auditLogsResponse.data.totalCount === 0 
                        ? 'No records found'
                        : `Showing ${((filters.pageNumber - 1) * filters.pageSize) + 1} to ${Math.min(filters.pageNumber * filters.pageSize, auditLogsResponse.data.totalCount)} of ${auditLogsResponse.data.totalCount} records`
                      }
                    </span>
                    
                    {auditLogsResponse.data.totalPages > 1 && (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFilterChange('pageNumber', Math.max(1, filters.pageNumber - 1))}
                          disabled={filters.pageNumber <= 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm">
                          Page {filters.pageNumber} of {auditLogsResponse.data.totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleFilterChange('pageNumber', Math.min(auditLogsResponse.data.totalPages, filters.pageNumber + 1))}
                          disabled={filters.pageNumber >= auditLogsResponse.data.totalPages}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
